import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState } from "react";
import { useQueryState } from "nuqs";
import { toast } from "sonner";

export type UserSortField = "name" | "email" | "created_at" | "credits_balance" | "last_active_at" | "last_purchase_at" | "eventCount" | "unlimited_expires_at" | "partnerCode" | "referredBy" | "referralCount";
export type SortDirection = "asc" | "desc";

export type UserFilterField = 
  | "name" 
  | "email" 
  | "phone" 
  | "userId" 
  | "memberType" 
  | "role" 
  | "credits"
  | "isUnlimited"
  | "tag"
  | "eventCount"
  | "inactiveDays"
  | "joinedDate"
  | "lastActiveDate"
   | "lastPurchaseDate"
   | "partnerCode"
   | "hasPartnerCode"
   | "referredBy"
   | "wasReferred"
   | "referralCount";

export type UserFilterOperator = "contains" | "equals" | ">=" | "<=" | ">" | "<" | "length>=" | "length<=" | "length=" | "length>" | "length<";

export interface UserFilterTag {
  id: string;
  field: UserFilterField;
  value: string;
  label: string;
  operator?: UserFilterOperator;
}

export interface UserTagInfo {
  id: string;
  name: string;
  color: string;
}

const USERS_PER_PAGE = 20;
const USERS_QUERY_KEY = "admin-users";

const STORAGE_KEY = "admin-user-filters";
const SORT_STORAGE_KEY = "admin-user-sort";

const getInitialFilters = (): string => {
  if (typeof window !== "undefined") {
    return localStorage.getItem(STORAGE_KEY) || "[]";
  }
  return "[]";
};

const getInitialSort = (): { field: UserSortField; direction: SortDirection } => {
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem(SORT_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Fall through to default
      }
    }
  }
  return { field: "created_at", direction: "desc" };
};

export const useAdminUsers = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  
  const initialSort = getInitialSort();
  const [sortField, setSortField] = useState<UserSortField>(initialSort.field);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSort.direction);

  // URL-synced filters as JSON array with localStorage fallback
  const [filtersParam, setFiltersParamRaw] = useQueryState("userFilters", {
    defaultValue: getInitialFilters(),
    parse: (value) => value || getInitialFilters(),
    serialize: (value) => value,
  });

  // Wrapper to persist to localStorage
  const setFiltersParam = (value: string) => {
    localStorage.setItem(STORAGE_KEY, value);
    setFiltersParamRaw(value);
  };

  // Parse filters from URL
  const filterTags: UserFilterTag[] = useMemo(() => {
    try {
      const parsed = JSON.parse(filtersParam);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [filtersParam]);

  // Add a new filter tag
  const addFilter = (field: UserFilterField, value: string, operator?: UserFilterOperator) => {
    // Check if this exact filter already exists
    const exists = filterTags.some(
      (t) => t.field === field && t.value.toLowerCase() === value.toLowerCase() && t.operator === operator
    );
    if (exists) return;

    const newTag: UserFilterTag = {
      id: `${field}-${value}-${operator || "default"}-${Date.now()}`,
      field,
      value,
      label: field,
      operator,
    };
    const newFilters = [...filterTags, newTag];
    setFiltersParam(JSON.stringify(newFilters));
    setCurrentPage(1);
  };

  // Remove a filter tag
  const removeFilter = (tagId: string) => {
    const newFilters = filterTags.filter((tag) => tag.id !== tagId);
    setFiltersParam(JSON.stringify(newFilters));
    setCurrentPage(1);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFiltersParam("[]");
    setCurrentPage(1);
  };

  const handleSort = (field: UserSortField) => {
    let newDirection: SortDirection;
    if (sortField === field) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
    } else {
      newDirection = "asc";
    }
    setSortField(field);
    setSortDirection(newDirection);
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ field, direction: newDirection }));
    setCurrentPage(1);
  };

  // Fetch users with memberships, roles, tags, and event counts
  const { data: users = [], isLoading } = useQuery({
    queryKey: [USERS_QUERY_KEY],
    queryFn: async () => {
      // Paginate through all profiles (Supabase default limit is 1000)
      const fetchAllProfiles = async () => {
        const allData: any[] = [];
        const pageSize = 1000;
        let from = 0;
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await supabase
            .from("profiles")
            .select(`
              *,
              user_memberships!left (
                credits_balance,
                is_unlimited,
                unlimited_expires_at,
                unlimited_starts_at,
                start_date,
                next_renewal_date
              )
            `)
            .order("created_at", { ascending: false })
            .range(from, from + pageSize - 1);

          if (error) throw error;
          if (data && data.length > 0) {
            allData.push(...data);
            from += pageSize;
            hasMore = data.length === pageSize;
          } else {
            hasMore = false;
          }
        }
        return allData;
      };

      // Fetch user roles with pagination
      const fetchAllRoles = async () => {
        const allData: { user_id: string; role: string }[] = [];
        const ps = 1000;
        let from = 0;
        let hasMore = true;

        while (hasMore) {
          const { data: batch, error } = await supabase
            .from("user_roles")
            .select("user_id, role")
            .range(from, from + ps - 1);

          if (error) throw error;
          if (batch && batch.length > 0) {
            allData.push(...batch);
            from += ps;
            hasMore = batch.length === ps;
          } else {
            hasMore = false;
          }
        }
        return allData;
      };

      // Fetch tag assignments with pagination
      const fetchAllTagAssignments = async () => {
        const allData: any[] = [];
        const ps = 1000;
        let from = 0;
        let hasMore = true;

        while (hasMore) {
          const { data: batch, error } = await supabase
            .from("user_tag_assignments")
            .select("user_id, tag:user_tags (id, name, color)")
            .range(from, from + ps - 1);

          if (error) throw error;
          if (batch && batch.length > 0) {
            allData.push(...batch);
            from += ps;
            hasMore = batch.length === ps;
          } else {
            hasMore = false;
          }
        }
        return allData;
      };

      // Fetch profiles, roles, tag assignments, and event counts in parallel
      const [profiles, roles, tagAssignments, eventCounts] = await Promise.all([
        fetchAllProfiles(),
        fetchAllRoles(),
        fetchAllTagAssignments(),
        // Use RPC function for event counts (bypasses RLS)
        supabase.rpc("get_user_event_counts").then(({ data, error }) => {
          if (error) {
            console.error("Failed to fetch event counts:", error);
            return [];
          }
          return data || [];
        }),
      ]);

      // Map roles to profiles
      const rolesMap = new Map<string, { role: string }[]>();
      roles.forEach((r) => {
        if (!rolesMap.has(r.user_id)) {
          rolesMap.set(r.user_id, []);
        }
        rolesMap.get(r.user_id)!.push({ role: r.role });
      });

      // Map tags to profiles
      const tagsMap = new Map<string, UserTagInfo[]>();
      tagAssignments.forEach((ta: any) => {
        if (!tagsMap.has(ta.user_id)) {
          tagsMap.set(ta.user_id, []);
        }
        if (ta.tag) {
          tagsMap.get(ta.user_id)!.push(ta.tag);
        }
      });

      // Map event counts from RPC result
      const eventCountMap = new Map<string, number>();
      eventCounts.forEach((ec: { user_id: string; event_count: number }) => {
        eventCountMap.set(ec.user_id, ec.event_count);
      });

       // Fetch partner code redemptions with partner code info
       const fetchAllRedemptions = async () => {
         const allData: any[] = [];
         const ps = 1000;
         let from = 0;
         let hasMore = true;

         while (hasMore) {
           const { data: batch, error } = await supabase
             .from("partner_code_redemptions")
             .select(`
               user_id,
               credits_granted,
               redeemed_at,
               partner_codes!inner (
                 code,
                 partner_name
               )
             `)
             .range(from, from + ps - 1);

           if (error) {
             console.error("Failed to fetch redemptions:", error);
             break;
           }
           if (batch && batch.length > 0) {
             allData.push(...batch);
             from += ps;
             hasMore = batch.length === ps;
           } else {
             hasMore = false;
           }
         }
         return allData;
       };

       // Fetch redemptions
       const redemptions = await fetchAllRedemptions();

       // Build redemptions map (user_id -> first redemption info)
       const redemptionsMap = new Map<string, {
         code: string;
         partnerName: string;
         creditsGranted: number;
         redeemedAt: string;
       }>();
       redemptions.forEach((r: any) => {
         // Only keep the first redemption per user
         if (!redemptionsMap.has(r.user_id) && r.partner_codes) {
           redemptionsMap.set(r.user_id, {
             code: r.partner_codes.code,
             partnerName: r.partner_codes.partner_name,
             creditsGranted: r.credits_granted,
             redeemedAt: r.redeemed_at,
           });
         }
       });

       // Build referrer names map (referred_by user_id -> name)
       const referrerIds = new Set<string>();
       profiles.forEach((p) => {
         if (p.referred_by) referrerIds.add(p.referred_by);
       });

       const referrerNamesMap = new Map<string, string>();
       if (referrerIds.size > 0) {
         // Fetch all referrer profiles
         const idsArray = Array.from(referrerIds);
         // Batch in chunks of 100 to avoid URL length limits
         for (let i = 0; i < idsArray.length; i += 100) {
           const chunk = idsArray.slice(i, i + 100);
           const { data: referrerProfiles, error } = await supabase
             .from("profiles")
             .select("id, first_name, last_name")
             .in("id", chunk);
           
           if (!error && referrerProfiles) {
             referrerProfiles.forEach((rp) => {
               const name = `${rp.first_name || ""} ${rp.last_name || ""}`.trim() || "Unknown";
               referrerNamesMap.set(rp.id, name);
             });
           }
         }
       }

       // Count referrals per user (how many people they referred)
       const referralCountMap = new Map<string, number>();
       profiles.forEach((p) => {
         if (p.referred_by) {
           referralCountMap.set(p.referred_by, (referralCountMap.get(p.referred_by) || 0) + 1);
         }
       });

       return profiles.map((profile) => ({
        ...profile,
        user_roles: rolesMap.get(profile.id) || [],
        tags: tagsMap.get(profile.id) || [],
        eventCount: eventCountMap.get(profile.id) || 0,
         partnerCode: redemptionsMap.get(profile.id) || null,
         referrerName: profile.referred_by ? referrerNamesMap.get(profile.referred_by) || null : null,
         referralCount: referralCountMap.get(profile.id) || 0,
      }));
    },
  });

  // Helper to compare values with operators
  const compareWithOperator = (value: number, filterValue: number, operator: UserFilterOperator): boolean => {
    switch (operator) {
      case "equals": return value === filterValue;
      case ">=": return value >= filterValue;
      case "<=": return value <= filterValue;
      case ">": return value > filterValue;
      case "<": return value < filterValue;
      default: return value === filterValue;
    }
  };

  // Helper to compare strings with operators
  const compareStringWithOperator = (value: string, filterValue: string, operator: UserFilterOperator): boolean => {
    switch (operator) {
      case "equals": return value.toLowerCase() === filterValue.toLowerCase();
      case "contains": return value.toLowerCase().includes(filterValue.toLowerCase());
      case "length>=": return value.length >= parseInt(filterValue);
      case "length<=": return value.length <= parseInt(filterValue);
      case "length=": return value.length === parseInt(filterValue);
      case "length>": return value.length > parseInt(filterValue);
      case "length<": return value.length < parseInt(filterValue);
      default: return value.toLowerCase().includes(filterValue.toLowerCase());
    }
  };

  // Helper to compare dates with operators
  const compareDateWithOperator = (dateStr: string | null, filterValue: string, operator: UserFilterOperator): boolean => {
    if (!dateStr) return false;
    const dateValue = new Date(dateStr).setHours(0, 0, 0, 0);
    const filterDate = new Date(filterValue).setHours(0, 0, 0, 0);
    if (isNaN(filterDate)) return false;
    
    switch (operator) {
      case "equals": return dateValue === filterDate;
      case ">=": return dateValue >= filterDate;
      case "<=": return dateValue <= filterDate;
      case ">": return dateValue > filterDate;
      case "<": return dateValue < filterDate;
      default: return dateValue === filterDate;
    }
  };

  // Apply client-side filtering and sorting
  const sortedUsers = useMemo(() => {
    let filtered = [...users];

    // Group filters by field
    const filtersByField = filterTags.reduce((acc, tag) => {
      if (!acc[tag.field]) acc[tag.field] = [];
      acc[tag.field].push({ value: tag.value.toLowerCase(), operator: tag.operator });
      return acc;
    }, {} as Record<string, { value: string; operator?: UserFilterOperator }[]>);

    // Apply filters (OR within same field, AND across different fields)
    Object.entries(filtersByField).forEach(([field, filters]) => {
      filtered = filtered.filter((user) => {
        const membership = Array.isArray(user.user_memberships) 
          ? user.user_memberships[0] 
          : user.user_memberships;
        const roles = user.user_roles || [];
        const tags = user.tags || [];

        switch (field) {
          case "name":
            const fullName = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
            return filters.some((f) => compareStringWithOperator(fullName, f.value, f.operator || "contains"));
          case "email":
            return filters.some((f) => compareStringWithOperator(user.email || "", f.value, f.operator || "contains"));
          case "phone":
            return filters.some((f) => compareStringWithOperator(user.phone || "", f.value, f.operator || "contains"));
          case "userId":
            return filters.some((f) => compareStringWithOperator(user.id || "", f.value, f.operator || "contains"));
          case "memberType":
            return filters.some((f) => user.member_type?.toLowerCase() === f.value);
          case "role":
            const userRoles = roles.map((r: { role: string }) => r.role?.toLowerCase());
            return filters.some((f) => userRoles.includes(f.value));
          case "credits":
            const credits = membership?.credits_balance || 0;
            return filters.some((f) => compareWithOperator(credits, parseFloat(f.value), f.operator || "equals"));
          case "isUnlimited":
            const isUnlimited = membership?.is_unlimited || false;
            const expiresAt = membership?.unlimited_expires_at;
            const isActive = isUnlimited && expiresAt && new Date(expiresAt) > new Date();
            const isExpired = isUnlimited && expiresAt && new Date(expiresAt) <= new Date();
            return filters.some((f) => {
              if (f.value === "active") return isActive;
              if (f.value === "expired") return isExpired;
              if (f.value === "never") return !isUnlimited;
              return false;
            });
          case "tag":
            const userTagNames = tags.map((t: UserTagInfo) => t.name.toLowerCase());
            return filters.some((f) => userTagNames.includes(f.value));
          case "eventCount":
            return filters.some((f) => compareWithOperator(user.eventCount, parseInt(f.value), f.operator || "equals"));
          case "inactiveDays":
            const daysSinceActive = user.last_active_at 
              ? Math.floor((Date.now() - new Date(user.last_active_at).getTime()) / (1000 * 60 * 60 * 24))
              : Infinity;
            return filters.some((f) => compareWithOperator(daysSinceActive, parseInt(f.value), f.operator || ">="));
          case "joinedDate":
            return filters.some((f) => compareDateWithOperator(user.created_at, f.value, f.operator || "equals"));
          case "lastActiveDate":
            return filters.some((f) => compareDateWithOperator(user.last_active_at, f.value, f.operator || "equals"));
          case "lastPurchaseDate":
            return filters.some((f) => compareDateWithOperator(user.last_purchase_at, f.value, f.operator || "equals"));
           case "partnerCode":
             const userCode = user.partnerCode?.code?.toLowerCase() || "";
             return filters.some((f) => userCode.includes(f.value.toLowerCase()));
           case "hasPartnerCode":
             return filters.some((f) => {
               if (f.value === "yes") return !!user.partnerCode;
               if (f.value === "no") return !user.partnerCode;
               return false;
             });
           case "referredBy":
             const referrerNameFilter = user.referrerName?.toLowerCase() || "";
             return filters.some((f) => referrerNameFilter.includes(f.value.toLowerCase()));
           case "wasReferred":
             return filters.some((f) => {
               if (f.value === "yes") return !!user.referred_by;
               if (f.value === "no") return !user.referred_by;
               return false;
             });
           case "referralCount":
             return filters.some((f) => compareWithOperator(user.referralCount || 0, parseInt(f.value), f.operator || "equals"));
          default:
            return true;
        }
      });
    });

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      const membershipA = Array.isArray(a.user_memberships) ? a.user_memberships[0] : a.user_memberships;
      const membershipB = Array.isArray(b.user_memberships) ? b.user_memberships[0] : b.user_memberships;

      switch (sortField) {
        case "name":
          const nameA = `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
          const nameB = `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        case "email":
          comparison = (a.email || "").localeCompare(b.email || "");
          break;
        case "created_at":
          if (!a.created_at && !b.created_at) comparison = 0;
          else if (!a.created_at) return 1;  // Always push nulls to end
          else if (!b.created_at) return -1; // Always push nulls to end
          else comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case "credits_balance":
          comparison = (membershipA?.credits_balance || 0) - (membershipB?.credits_balance || 0);
          break;
        case "last_active_at":
          if (!a.last_active_at && !b.last_active_at) comparison = 0;
          else if (!a.last_active_at) return 1;  // Always push nulls to end
          else if (!b.last_active_at) return -1; // Always push nulls to end
          else comparison = new Date(a.last_active_at).getTime() - new Date(b.last_active_at).getTime();
          break;
        case "last_purchase_at":
          if (!a.last_purchase_at && !b.last_purchase_at) comparison = 0;
          else if (!a.last_purchase_at) return 1;  // Always push nulls to end
          else if (!b.last_purchase_at) return -1; // Always push nulls to end
          else comparison = new Date(a.last_purchase_at).getTime() - new Date(b.last_purchase_at).getTime();
          break;
        case "eventCount":
          comparison = a.eventCount - b.eventCount;
          break;
         case "partnerCode":
           const codeA = a.partnerCode?.code?.toLowerCase() || "";
           const codeB = b.partnerCode?.code?.toLowerCase() || "";
           comparison = codeA.localeCompare(codeB);
           break;
         case "referredBy":
           const refNameA = a.referrerName?.toLowerCase() || "";
           const refNameB = b.referrerName?.toLowerCase() || "";
           comparison = refNameA.localeCompare(refNameB);
           break;
         case "referralCount":
           comparison = (a.referralCount || 0) - (b.referralCount || 0);
           break;
        case "unlimited_expires_at":
          const expiresA = membershipA?.unlimited_expires_at;
          const expiresB = membershipB?.unlimited_expires_at;
          if (!expiresA && !expiresB) comparison = 0;
          else if (!expiresA) return 1;  // Always push nulls to end
          else if (!expiresB) return -1; // Always push nulls to end
          else comparison = new Date(expiresA).getTime() - new Date(expiresB).getTime();
          break;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [users, filterTags, sortField, sortDirection]);

  // Pagination
  const totalUsers = sortedUsers.length;
  const totalPages = Math.ceil(totalUsers / USERS_PER_PAGE);
  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.functions.invoke("admin-user-actions", {
        body: { action: "delete", userId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["admin-audit-logs"] });
      toast.success("User deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete user: ${error.message}`);
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: {
      first_name: string;
      last_name: string;
      phone: string;
      member_type: string;
      credits_balance: number;
      is_unlimited: boolean;
      unlimited_expires_at: string | null;
      is_admin: boolean;
    }}) => {
      const { data: result, error } = await supabase.functions.invoke("admin-user-actions", {
        body: { action: "update", userId, data },
      });
      if (error) {
        // Surface the edge function's real error message instead of "non-2xx"
        const body = await (error as { context?: Response }).context?.json?.().catch(() => null);
        throw new Error(body?.error || error.message);
      }
      if (result?.error) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["admin-audit-logs"] });
      toast.success("User updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update user: ${error.message}`);
    },
  });

  return {
    users: paginatedUsers,
    allFilteredUsers: sortedUsers,
    filteredUserIds: sortedUsers.map((u) => u.id),
    isLoading,
    isEmpty: !isLoading && users.length === 0,
    // Filters
    filterTags,
    addFilter,
    removeFilter,
    clearAllFilters,
    setFilters: (filters: UserFilterTag[]) => setFiltersParam(JSON.stringify(filters)),
    // Pagination
    currentPage,
    setCurrentPage,
    totalPages,
    totalUsers,
    usersPerPage: USERS_PER_PAGE,
    // Sorting
    sortField,
    sortDirection,
    handleSort,
    // Actions
    deleteUser: deleteUserMutation.mutateAsync,
    isDeleting: deleteUserMutation.isPending,
    deletingUserId: deleteUserMutation.variables || null,
    updateUser: updateUserMutation.mutateAsync,
    isUpdating: updateUserMutation.isPending,
  };
};
